import { HexColorPicker } from 'react-colorful';
import { useState } from 'react';
import { MousePointer2, Type, Stamp, Pen, Undo2, Download, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type AnnotationMode = 'pointer' | 'text' | 'stamp' | 'freehand';
export type StampType = 'approved' | 'approved_as_noted' | 'revise_resubmit' | 'rejected';

export const STAMP_LABELS: Record<StampType, string> = {
  approved: 'Approved',
  approved_as_noted: 'Approved as Noted',
  revise_resubmit: 'Revise & Resubmit',
  rejected: 'Rejected',
};

interface AnnotationToolbarProps {
  mode: AnnotationMode;
  stampType: StampType;
  freehandColor: string;
  onModeChange: (mode: AnnotationMode) => void;
  onStampTypeChange: (type: StampType) => void;
  onColorChange: (color: string) => void;
  onUndo: () => void;
  onExportPdf: () => void;
  onSendForReview: () => void;
  canUndo: boolean;
  exporting?: boolean;
}

const TOOL_BUTTONS: { mode: AnnotationMode; icon: React.ElementType; label: string }[] = [
  { mode: 'pointer', icon: MousePointer2, label: 'Pointer' },
  { mode: 'text', icon: Type, label: 'Text Comment' },
  { mode: 'stamp', icon: Stamp, label: 'Stamp' },
  { mode: 'freehand', icon: Pen, label: 'Freehand' },
];

export function AnnotationToolbar({
  mode,
  stampType,
  freehandColor,
  onModeChange,
  onStampTypeChange,
  onColorChange,
  onUndo,
  onExportPdf,
  onSendForReview,
  canUndo,
  exporting = false,
}: AnnotationToolbarProps) {
  const [colorOpen, setColorOpen] = useState(false);

  return (
    <div className="flex items-center gap-2 p-2 bg-background border-b flex-wrap">
      {/* Tool mode buttons */}
      <div className="flex items-center gap-1 border rounded-md p-0.5">
        {TOOL_BUTTONS.map(({ mode: m, icon: Icon, label }) => (
          <Button
            key={m}
            variant={mode === m ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            title={label}
            onClick={() => onModeChange(m)}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="sr-only">{label}</span>
          </Button>
        ))}
      </div>

      {/* Stamp type selector — only shown in stamp mode */}
      {mode === 'stamp' && (
        <Select value={stampType} onValueChange={(v) => onStampTypeChange(v as StampType)}>
          <SelectTrigger className="h-7 text-xs w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(STAMP_LABELS) as [StampType, string][]).map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-xs">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Freehand color picker — only shown in freehand mode */}
      {mode === 'freehand' && (
        <Popover open={colorOpen} onOpenChange={setColorOpen}>
          <PopoverTrigger asChild>
            <button
              className="h-7 w-7 rounded border border-input flex-shrink-0"
              style={{ backgroundColor: freehandColor }}
              title="Freehand color"
            />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <HexColorPicker color={freehandColor} onChange={onColorChange} />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-mono">{freehandColor}</span>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Action buttons */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2"
        onClick={onUndo}
        disabled={!canUndo}
        title="Undo last annotation"
      >
        <Undo2 className="w-3.5 h-3.5" />
        <span className="ml-1 text-xs">Undo</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={onExportPdf}
        disabled={exporting}
        title="Export flat PDF with annotations baked in"
      >
        <Download className="w-3 h-3 mr-1" />
        {exporting ? 'Exporting…' : 'Export PDF'}
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={onSendForReview}
        title="Send annotated PDF to engineers for review"
      >
        <Send className="w-3 h-3 mr-1" />
        Send for Review
      </Button>
    </div>
  );
}
